/*
 * Pornirea sesiunii si singurul loc care schimba starea de autentificare.
 *
 * Starea nu sta in `useState`, ci in modulul `api/session`, la care se aboneaza
 * prin `useSyncExternalStore`. Motivul: clientul de api are nevoie de token in
 * mijlocul unui `fetch`, unde nu exista hook-uri. Cu starea in React ar trebui
 * ori pasat tokenul prin fiecare apel, ori tinut oricum intr-o variabila de
 * modul - adica acelasi lucru, dar cu doua surse care se pot desincroniza.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { setSessionLostHandler } from '../api/client';
import { toApiError } from '../api/errors';
import * as session from '../api/session';
import * as shop from '../api/shop';
import { useToasts } from '../components/ToastContext';
import { AuthContext, type AuthPhase, type AuthValue } from './AuthContext';
import { onTicket, takeTicket } from './ssoTicket';
import { takeVerifier } from './ssoBinding';

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const [phase, setPhase] = useState<AuthPhase>('booting');
  const [ssoError, setSsoError] = useState<AuthValue['ssoError']>(null);
  const { push } = useToasts();

  /* StrictMode monteaza de doua ori in dezvoltare. Fara paza asta, pornirea ar
     trimite doua cereri `me` si - mai rau - doua schimburi de bilet. */
  const bootStarted = useRef(false);

  useEffect(() => {
    if (bootStarted.current) return;
    bootStarted.current = true;

    /*
     * Schimbul biletului, scos din pornire ca sa poata fi refolosit.
     *
     * Pe web se cheama o singura data, cu biletul care era deja in adresa. In
     * carcasa nativa acelasi drum trebuie parcurs si mai tarziu: biletul soseste
     * abia dupa ce omul se intoarce de la IdP printr-o adresa proprie, cand
     * pornirea s-a incheiat demult.
     *
     * Sesiunea se goleste explicit la esec: un schimb ratat poate lasa un jwt
     * adoptat si un `me` cazut, iar jumatatea aia de sesiune ar trece de
     * verificarea de rutare si ar pica pe primul ecran.
     */
    const exchange = async (ticket: string): Promise<void> => {
      try {
        /* Secretul tinut de fila la pornirea SSO (auth/ssoBinding.ts): leaga
           biletul de clientul care l-a cerut. */
        session.adopt(await shop.ssoExchange(ticket, takeVerifier()));
        session.setIdentity(await shop.me());
        setSsoError(null);
      } catch (cause) {
        const error = toApiError(cause);
        session.clear();
        setSsoError({ message: error.message, code: error.code });
      }
    };

    /* Pe web nu se declanseaza niciodata - acolo biletul era in adresa inainte ca
       React sa deseneze, deci l-a luat deja `takeTicket()` mai jos. */
    const stopListening = onTicket((ticket) => {
      void (async () => {
        await exchange(ticket);
        setPhase('ready');
      })();
    });

    void (async () => {
      const ticket = takeTicket();

      if (ticket !== null) {
        await exchange(ticket);
        setPhase('ready');
        return;
      }

      if (session.restore()) {
        /*
         * Oglinda din sessionStorage tine doar cu ce se cere un jwt nou, nu
         * jwt-ul. `GET /shop/auth/me` il cere intai prin refresh (clientul vede
         * ca nu are token, dar are cu ce-l cere), apoi verifica sesiunea inainte
         * de a desena meniul: altfel omul vede navigatia si numele lui, iar
         * primul ecran il arunca la login.
         */
        try {
          session.setIdentity(await shop.me());
        } catch {
          session.clear();
        }
      }

      setPhase('ready');
    })();

    return stopListening;
  }, []);

  /* Sesiunea pierduta in timpul lucrului: `session.clear()` a fost deja chemat
     de client, iar abonamentul de mai sus reface rutarea catre login. Aici
     ramane doar de spus omului de ce s-a schimbat ecranul sub el. */
  useEffect(() => {
    setSessionLostHandler(() => {
      if (session.isAuthenticated()) return;
      push('Your session has ended. Please sign in again.', 'error');
    });
    return () => setSessionLostHandler(() => {});
  }, [push]);

  const signIn = useCallback(async (username: string, password: string) => {
    session.adopt(await shop.login(username, password));
    /* Numele si emailul nu vin la login, ci de la `me`. Se cer inainte ca
       ecranul sa se schimbe, ca navigatia sa nu apara cu "Unknown user" si sa
       se corecteze o clipa mai tarziu. */
    session.setIdentity(await shop.me());
  }, []);

  const signOut = useCallback(async () => {
    /* Iesirea din cont nu are voie sa esueze pentru om: se cere api-ului sa
       invalideze refresh tokenul, dar sesiunea locala se sterge oricum. */
    try {
      await shop.logout();
    } catch {
      /* Contractul spune ca ruta reuseste si cu token mort; daca totusi cade,
         singurul lucru care conteaza e ca tokenul dispare de pe statie. */
    }
    session.clear();
  }, []);

  const dismissSsoError = useCallback(() => setSsoError(null), []);

  const value = useMemo<AuthValue>(
    () => ({
      phase,
      authenticated: state.jwt !== null,
      user: state.user,
      ssoError,
      signIn,
      signOut,
      dismissSsoError,
    }),
    [phase, state.jwt, state.user, ssoError, signIn, signOut, dismissSsoError],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
